/**
 * EffectsColorTotals.js
 * Section 2 of ResultEffects: totals grouped by Base color
 * (Orange, Green, Purple, Red, Blue). Red is marked as debuff
 * and excluded from the final Total* column.
 */
import React, { useState } from 'react';
import {
    BASE_COLORS, DEBUFF_COLORS,
    getStyle, getEffectsFromScenario, parseNumeric, collectEffectNames, fmt,
    stickyLeft, stickyRight, ScrollTable, SectionHeader, EffectNameCell,
} from './effectsHelpers';

// Effects whose values are per-skill and must NOT be summed across skills
const NON_SUMMABLE_EFFECTS = ['duration'];
const isNonSummable = (name) =>
    NON_SUMMABLE_EFFECTS.some(ns => name.toLowerCase().includes(ns));

const EffectsColorTotals = ({ baseSkills }) => {
    const [open, setOpen] = useState(true);
    if (baseSkills.length === 0) return null;

    const presentColors = BASE_COLORS.filter(c => baseSkills.some(s => s.color === c));
    // Only show effects that can be meaningfully summed
    const effectNames = collectEffectNames(baseSkills).filter(n => !isNonSummable(n));
    if (effectNames.length === 0 || presentColors.length === 0) return null;

    // Sum per color per effect (skip non-summable)
    const sumByColor = {};
    for (const color of presentColors) {
        sumByColor[color] = {};
        const colorSkills = baseSkills.filter(s => s.color === color);
        for (const name of effectNames) {
            let sum = 0, hasAny = false;
            colorSkills.forEach(s => {
                const fx = getEffectsFromScenario(s);
                (fx[name] || []).forEach(([val]) => {
                    const n = parseNumeric(val);
                    if (n !== null) { sum += n; hasAny = true; }
                });
            });
            sumByColor[color][name] = hasAny ? sum : null;
        }
    }

    // Grand total excluding debuff colors
    const totalByEffect = {};
    for (const name of effectNames) {
        let sum = 0, hasAny = false;
        for (const color of presentColors) {
            if (DEBUFF_COLORS.includes(color)) continue;
            const v = sumByColor[color][name];
            if (v !== null) { sum += v; hasAny = true; }
        }
        totalByEffect[name] = hasAny ? sum : null;
    }
    const hasTotal = Object.values(totalByEffect).some(v => v !== null);

    return (
        <div className="space-y-2">
            <SectionHeader
                open={open}
                onToggle={() => setOpen(o => !o)}
                dotColor="bg-violet-400"
                title="Totals by Color"
                subtitle={`${presentColors.length} color${presentColors.length !== 1 ? 's' : ''} · ${baseSkills.length} skill${baseSkills.length !== 1 ? 's' : ''}`}
            />
            {open && (
                <div className="rounded-xl border border-slate-700">
                    <ScrollTable minWidth={`${160 + presentColors.length * 100 + (hasTotal ? 90 : 0)}px`}>
                        <thead>
                            <tr className="bg-slate-900 border-b border-slate-700">
                                <th className={`${stickyLeft} bg-slate-900 px-4 py-2.5 text-[9px]
                                    font-bold uppercase tracking-widest text-slate-500 w-40
                                    border-r border-slate-700/60`}>
                                    Effect
                                </th>
                                {presentColors.map(color => {
                                    const st = getStyle(color);
                                    return (
                                        <th key={color} className="px-4 py-2.5 text-center min-w-[100px]">
                                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5
                                                rounded-full text-[9px] font-bold border ${st.pill}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                                                {color.replace(' Skills', '').replace(' Skill', '')}
                                                {DEBUFF_COLORS.includes(color) && (
                                                    <span className="text-rose-400">↓</span>
                                                )}
                                            </span>
                                        </th>
                                    );
                                })}
                                {hasTotal && (
                                    <th className={`${stickyRight} bg-slate-900 px-4 py-2.5 text-[9px]
                                        font-bold uppercase tracking-widest text-amber-500 w-24
                                        border-l border-slate-700/60`}>
                                        Total*
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {effectNames.map((name, rowIdx) => (
                                <tr key={name}
                                    className={`border-b border-slate-700/40 last:border-0
                                        ${rowIdx % 2 === 0 ? 'bg-slate-800/40' : 'bg-slate-800/10'}`}>
                                    <EffectNameCell name={name} rowIdx={rowIdx} />
                                    {presentColors.map(color => (
                                        <td key={color} className="px-4 py-2.5 text-center">
                                            {sumByColor[color][name] !== null
                                                ? <span className={`font-mono font-bold text-[13px] ${DEBUFF_COLORS.includes(color) ? 'text-rose-400' : 'text-emerald-400'
                                                    }`}>{fmt(sumByColor[color][name])}</span>
                                                : <span className="text-slate-700 text-[11px]">—</span>}
                                        </td>
                                    ))}
                                    {hasTotal && (
                                        <td className={`${stickyRight} px-4 py-2.5 text-center border-l border-slate-700/40
                                            ${rowIdx % 2 === 0 ? 'bg-slate-800/90' : 'bg-slate-900/80'}`}>
                                            {totalByEffect[name] !== null
                                                ? <span className="font-mono font-bold text-amber-300 text-[13px]">{fmt(totalByEffect[name])}</span>
                                                : <span className="text-slate-700 text-[11px]">—</span>}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </ScrollTable>
                    {hasTotal && (
                        <div className="px-4 py-2 bg-slate-900/30 border-t border-slate-700">
                            <p className="text-[9px] text-slate-600 italic">
                                * Total excludes Red Skills (debuffs). Assumes all conditions met simultaneously.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EffectsColorTotals;
